import { Link, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { ApolloError } from "@apollo/client";
import { Select } from "@normasteaching/norma-ui";
import { Layout } from "../UI/Layout/Layout.tsx";
import { Card } from "../UI/Card.tsx";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { BreadCrumbsWrapper } from "../UI/Layout/BreadCrumbsWrapper.tsx";
import { BreadCrumbsItem } from "../UI/Layout/BreadCrumbsItem.tsx";
import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import {
  CheckIcon,
  ClipboardDocumentCheckIcon,
  TrashIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { IconButton } from "../UI/IconButton.tsx";
import { InfoTooltip } from "../UI/InfoTooltip.tsx";
import { useConfirm } from "../UI/Feedback/useConfirm.ts";
import { useToast } from "../UI/Feedback/useToast.ts";
import {
  OnboardingLiveCreationIntentItemFragment,
  OnboardingLiveCreationIntentStatus,
  OnboardingLivePlanIssueCode,
  OnboardingLivePlanIssueSeverity,
  OnboardingLiveSlotCreationStatus,
  OnboardingLiveSlotPlanItemFragment,
  OnboardingLiveSlotPlanStatus,
} from "../gql/graphql.ts";
import { getFragmentData } from "../gql";
import { OnboardingLiveSlotPlanFragment } from "./OnboardingLiveFragments.ts";
import { useOnboardingLiveCreationIntent } from "./useOnboardingLiveCreationIntent.ts";
import { useOnboardingLiveConductors } from "./useOnboardingLiveConductors.ts";
import { useAllUserGroups } from "../UserGroup/useAllUserGroups.ts";
import { OnboardingLiveSlotPicker } from "./SlotPicker/OnboardingLiveSlotPicker.tsx";
import { ConductorSelect } from "./ConductorSelect.tsx";
import { PlanIssueList } from "./PlanIssueList.tsx";
import { PlanStatusBadge } from "./PlanStatusBadge.tsx";
import { formatSlot, UserGroupAvailability } from "./onboardingLiveTime.ts";
import {
  onboardingLiveDetailPathOf,
  OnboardingLivesTab,
} from "./OnboardingLivePaths.ts";
import { CreationIntentStatusBadge } from "./CreationIntentStatusBadge.tsx";
import { useBackToOnboardingLivesList } from "./List/onboardingLivesListNavigation.ts";

type IntentSlot = OnboardingLiveCreationIntentItemFragment["slots"][number];

const creationStatusLabels: Record<OnboardingLiveSlotCreationStatus, string> = {
  [OnboardingLiveSlotCreationStatus.Pending]: "In attesa",
  [OnboardingLiveSlotCreationStatus.Processing]: "In creazione",
  [OnboardingLiveSlotCreationStatus.Created]: "Creata",
  [OnboardingLiveSlotCreationStatus.Failed]: "Non creata",
};

function planOf(
  plan: IntentSlot["lastCheckPlan"],
): OnboardingLiveSlotPlanItemFragment | undefined {
  return getFragmentData(OnboardingLiveSlotPlanFragment, plan) ?? undefined;
}

function sameUserGroupSameDayWarningsOf(slots: IntentSlot[]): IntentSlot[] {
  return slots.filter((s) =>
    (planOf(s.lastCheckPlan)?.issues ?? []).some(
      (i) => i.code === OnboardingLivePlanIssueCode.SameUserGroupSameDay,
    ),
  );
}

function SlotConductor({
  plan,
}: {
  plan?: OnboardingLiveSlotPlanItemFragment;
}) {
  const { teacherNameOf } = useOnboardingLiveConductors();
  if (!plan?.teacherId) return null;
  return (
    <p className={"text-xs"}>
      Conduttore:{" "}
      <span className={"font-semibold"}>{teacherNameOf(plan.teacherId)}</span>
      {plan.forcedRoster ? " (turno forzato)" : ""}
    </p>
  );
}

function SlotRow({
  slot,
  isDraft,
  onChangeConductor,
  onRemove,
}: {
  slot: IntentSlot;
  isDraft: boolean;
  onChangeConductor: (teacherId?: string) => void;
  onRemove: () => void;
}) {
  const { teacherNameOf } = useOnboardingLiveConductors();
  const plan = planOf(
    slot.creationStatus
      ? (slot.creationPlan ?? slot.lastCheckPlan)
      : slot.lastCheckPlan,
  );
  return (
    <tr className={"border-b align-top"}>
      <td className={"py-3 pr-4 font-medium whitespace-nowrap"}>
        {formatSlot(slot.startUtc)}
      </td>
      <td className={"py-3 pr-4 min-w-[220px]"}>
        {isDraft ? (
          <ConductorSelect
            value={slot.requestedTeacherId}
            onChange={onChangeConductor}
          />
        ) : slot.requestedTeacherId ? (
          teacherNameOf(slot.requestedTeacherId)
        ) : (
          "Automatico"
        )}
      </td>
      <td className={"py-3 pr-4 space-y-1 min-w-[280px]"}>
        {plan ? (
          <>
            <PlanStatusBadge status={plan.status} />
            <SlotConductor plan={plan} />
            <PlanIssueList issues={plan.issues} />
          </>
        ) : (
          <span className={"text-xs text-slate-500"}>Da verificare</span>
        )}
      </td>
      {isDraft ? null : (
        <td className={"py-3 pr-4 text-sm"}>
          <p>
            {slot.creationStatus
              ? creationStatusLabels[slot.creationStatus]
              : "-"}
          </p>
          {slot.onboardingLiveId ? (
            <Link
              className={"underline text-xs"}
              to={onboardingLiveDetailPathOf(slot.onboardingLiveId)}
            >
              Apri live
            </Link>
          ) : null}
          {slot.creationFailureReason ? (
            <p className={"text-xs text-red-700"}>
              {slot.creationFailureReason}
            </p>
          ) : null}
        </td>
      )}
      {isDraft ? (
        <td className={"py-3"}>
          <IconButton
            label={"Rimuovi slot"}
            icon={TrashIcon}
            tone={"danger"}
            onClick={onRemove}
          />
        </td>
      ) : null}
    </tr>
  );
}

function SameDayWarningBanner({ slots }: { slots: IntentSlot[] }) {
  if (slots.length <= 0) return null;
  return (
    <div
      className={
        "rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
      }
    >
      <p className={"font-semibold"}>
        Attenzione: {slots.length} live sono nello stesso giorno di un'altra
        live dello stesso user group
      </p>
      <p>{slots.map((s) => formatSlot(s.startUtc)).join(" · ")}</p>
    </div>
  );
}

export function OnboardingLivePlanScreen() {
  const { intentId } = useParams<{ intentId: string }>();
  if (!intentId) throw new Error("intentId is required");
  const askConfirmation = useConfirm();
  const showToast = useToast();
  const backToList = useBackToOnboardingLivesList(OnboardingLivesTab.Plans);
  const { userGroups } = useAllUserGroups();
  const {
    intent,
    loading,
    changeUserGroup,
    addSlots,
    updateSlot,
    removeSlots,
    check,
    confirm,
    cancel,
    checking,
    confirming,
    cancelling,
  } = useOnboardingLiveCreationIntent(intentId);

  if (loading && !intent)
    return (
      <Layout>
        <Card>
          <LoadingRows />
        </Card>
      </Layout>
    );
  if (!intent)
    return (
      <Layout>
        <Card>Pianificazione non trovata</Card>
      </Layout>
    );

  const isDraft = intent.status === OnboardingLiveCreationIntentStatus.Draft;
  const userGroup = userGroups.find((g) => g.id === intent.userGroupId);
  const plans = intent.slots.map((s) => planOf(s.lastCheckPlan));
  const isCheckUpToDate =
    !!intent.lastCheckedAtUtc &&
    intent.slots.length > 0 &&
    plans.every((p) => !!p);
  const blockedSlotsCount = plans.filter(
    (p) => p?.status === OnboardingLiveSlotPlanStatus.Blocked,
  ).length;
  const warningsCount = plans.filter((p) =>
    p?.issues.some(
      (i) => i.severity === OnboardingLivePlanIssueSeverity.Warning,
    ),
  ).length;
  const sameDayWarningSlots = sameUserGroupSameDayWarningsOf(intent.slots);

  async function withErrorToast(action: () => Promise<void>) {
    try {
      await action();
    } catch (e) {
      showToast((e as ApolloError)?.message ?? "Errore", "error");
    }
  }

  async function onConfirm() {
    const confirmed = await askConfirmation({
      title: `Creare ${intent?.slots.length} live onboarding?`,
      message: (
        <div className={"space-y-2"}>
          <p>
            Le eventuali classi di gruppo indicate verranno riassegnate, i
            conduttori riceveranno una mail per ogni live e verranno bloccati su
            Shiftbase.
          </p>
          {sameDayWarningSlots.length > 0 ? (
            <SameDayWarningBanner slots={sameDayWarningSlots} />
          ) : null}
        </div>
      ),
      confirmLabel: "Crea le live",
    });
    if (!confirmed) return;
    await withErrorToast(confirm);
  }

  async function onCancel() {
    const confirmed = await askConfirmation({
      title: "Annullare la pianificazione?",
      message:
        "Nessuna live verrà creata. La pianificazione resterà nello storico.",
      confirmLabel: "Annulla pianificazione",
      isDestructive: true,
    });
    if (!confirmed) return;
    await withErrorToast(cancel);
  }

  return (
    <Layout>
      <Card>
        <ScreenTitleBlock
          title={"Pianificazione live onboarding"}
          breadCrumbs={
            <BreadCrumbsWrapper>
              <BreadCrumbsItem onClick={backToList} title={"Live onboarding"} />
              <BreadCrumbsItem title={"Pianificazione"} />
            </BreadCrumbsWrapper>
          }
        />
        <div className={"mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm"}>
          <div>
            <p className={"text-slate-500"}>Stato</p>
            <CreationIntentStatusBadge status={intent.status} />
          </div>
          <div>
            <p className={"text-slate-500"}>Creata</p>
            <p>
              {dayjs(intent.createdAtUtc).format("DD/MM/YYYY HH:mm")} da{" "}
              {intent.createdByEmail ?? "-"}
            </p>
          </div>
          {intent.confirmedAtUtc ? (
            <div>
              <p className={"text-slate-500"}>Confermata</p>
              <p>
                {dayjs(intent.confirmedAtUtc).format("DD/MM/YYYY HH:mm")} da{" "}
                {intent.confirmedByEmail ?? "-"}
              </p>
            </div>
          ) : null}
          {intent.cancelledAtUtc ? (
            <div>
              <p className={"text-slate-500"}>Annullata</p>
              <p>
                {dayjs(intent.cancelledAtUtc).format("DD/MM/YYYY HH:mm")} da{" "}
                {intent.cancelledByEmail ?? "-"}
              </p>
            </div>
          ) : null}
          <div>
            <p className={"flex items-center gap-1 text-slate-500"}>
              User group
              <InfoTooltip
                text={
                  "Le live vengono create per gli studenti di questo user group. Finché la pianificazione è in bozza puoi cambiarlo: la griglia mostra le sue fasce orarie."
                }
              />
            </p>
            {isDraft ? (
              <Select<string>
                options={userGroups
                  .filter((g) => g.onboardingLiveRequired)
                  .map((g) => ({ id: g.id, name: g.name }))}
                value={intent.userGroupId}
                onChange={(v) => v && withErrorToast(() => changeUserGroup(v))}
              />
            ) : (
              <p className={"font-semibold"}>
                {userGroup?.name ?? intent.userGroupId}
              </p>
            )}
          </div>
        </div>
      </Card>

      {intent.status === OnboardingLiveCreationIntentStatus.Confirmed ? (
        <div
          className={
            "mt-4 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900"
          }
        >
          Creazione in corso: le live vengono create nei prossimi minuti, la
          pagina si aggiorna da sola.
        </div>
      ) : null}
      {!isDraft ? (
        <div className={"mt-4"}>
          <SameDayWarningBanner slots={sameDayWarningSlots} />
        </div>
      ) : null}

      {isDraft ? (
        <div className={"mt-4"}>
          <OnboardingLiveSlotPicker
            userGroupId={intent.userGroupId}
            availability={
              userGroup?.availability as UserGroupAvailability | undefined
            }
            plannedSlots={intent.slots.map((slot) => ({
              slotId: slot.id,
              startUtc: slot.startUtc,
              status: planOf(slot.lastCheckPlan)?.status,
            }))}
            addSlots={addSlots}
            removeSlot={(slotId) => removeSlots([slotId])}
          />
        </div>
      ) : null}

      <div className={"mt-4"}>
        <Card>
          <div
            className={
              "flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4"
            }
          >
            <div>
              <h2 className={"flex items-center gap-1.5 font-bold text-lg"}>
                Slot pianificati ({intent.slots.length})
                <InfoTooltip
                  text={
                    "La verifica controlla conduttori, turni e classi di gruppo da riassegnare. Ogni modifica agli slot richiede una nuova verifica prima di poter confermare."
                  }
                />
              </h2>
              {isDraft ? (
                <p className={"text-sm text-slate-600"}>
                  {isCheckUpToDate
                    ? `Verifica del ${dayjs(intent.lastCheckedAtUtc).format("DD/MM HH:mm")}: ${blockedSlotsCount} bloccati, ${warningsCount} con avvisi`
                    : "Verifica la pianificazione per vedere conduttori, riassegnazioni e conflitti. Ogni modifica richiede una nuova verifica."}
                </p>
              ) : null}
            </div>
            {isDraft ? (
              <div className={"flex shrink-0 items-center gap-2"}>
                <IconButton
                  label={"Annulla pianificazione"}
                  icon={XCircleIcon}
                  tone={"danger"}
                  loading={cancelling}
                  onClick={onCancel}
                />
                <IconButton
                  label={"Verifica"}
                  icon={ClipboardDocumentCheckIcon}
                  loading={checking}
                  disabled={intent.slots.length <= 0}
                  disabledReason={"aggiungi almeno uno slot"}
                  onClick={() => withErrorToast(check)}
                />
                <PrimaryButton
                  icon={CheckIcon}
                  label={"Conferma e crea"}
                  loading={confirming}
                  disabled={!isCheckUpToDate || blockedSlotsCount > 0}
                  disabledReason={
                    !isCheckUpToDate
                      ? "Verifica prima la pianificazione"
                      : "Risolvi gli slot bloccati"
                  }
                  onClick={onConfirm}
                />
              </div>
            ) : null}
          </div>
          {isDraft && isCheckUpToDate ? (
            <div className={"mb-4"}>
              <SameDayWarningBanner slots={sameDayWarningSlots} />
            </div>
          ) : null}
          {intent.slots.length <= 0 ? (
            <p className={"text-sm text-slate-500"}>
              Nessuno slot: aggiungili qui sopra.
            </p>
          ) : (
            <div className={"overflow-x-auto"}>
              <table className={"w-full text-sm"}>
                <thead>
                  <tr className={"text-left text-slate-500 border-b"}>
                    <th className={"py-2 pr-4"}>Data e ora</th>
                    <th className={"py-2 pr-4"}>
                      <span className={"flex items-center gap-1"}>
                        Conduttore richiesto
                        <InfoTooltip
                          text={
                            "Automatico: viene scelto chi ha il tag Live Onboarding e ha condotto meno live nelle ultime 4 settimane. Se scegli tu un conduttore, il turno su Shiftbase viene creato anche se non era disponibile."
                          }
                        />
                      </span>
                    </th>
                    <th className={"py-2 pr-4"}>
                      <span className={"flex items-center gap-1"}>
                        Esito verifica
                        <InfoTooltip
                          text={
                            "OK: lo slot si può creare. Con avvisi: si può creare, ma controlla i dettagli. Bloccato: va risolto prima di confermare."
                          }
                        />
                      </span>
                    </th>
                    {!isDraft ? (
                      <th className={"py-2 pr-4"}>Creazione</th>
                    ) : null}
                    {isDraft ? <th className={"py-2"} /> : null}
                  </tr>
                </thead>
                <tbody>
                  {intent.slots.map((slot) => (
                    <SlotRow
                      key={slot.id}
                      slot={slot}
                      isDraft={isDraft}
                      onChangeConductor={(teacherId) =>
                        withErrorToast(() =>
                          updateSlot(slot.id, {
                            startUtc: slot.startUtc,
                            teacherId,
                          }),
                        )
                      }
                      onRemove={() =>
                        withErrorToast(() => removeSlots([slot.id]))
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
